import React, { useEffect, useState } from 'react';
import useUiContext from '@groupefungo/react-apollo.core/ui/UseContext';
import ReactPixel from 'react-facebook-pixel';
import * as TagManager from 'react-gtm-module';
import Link from '@material-ui/core/Link';
import ReactGA from 'react-ga4';
import useClientsEventBookingContext from './hooks/useClientsEventBookingContext';
import useCurrentClientProfileContext from '../ui/useCurrentClientProfileContext';
import useClientsEventBookingFormMutation from './hooks/useClientsEventBookingFormMutation';
import ExternalLinkIcon from '../../images/new-browser-tab-icon.svg';
import BookingConfirmed from '../../images/booking-step-confirmed-2022.png';

const AppointmentConfirmed = () => {
  const { Grid, Button, InputMui, SelectMui } = useUiContext();
  const { clientsEventBooking } = useClientsEventBookingContext();
  const { mutate } = useClientsEventBookingFormMutation();
  const { currentClient: { id: currentClientId } } = useCurrentClientProfileContext();

  const [stylesCount, setStylesCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);

  const [state, setState] = useState({
    commentText: '',
    pinterestLink: '',
    notes: '',
    referral: '',
    other: '',
    errors: {
      notes: false,
      commentText: false,
      referral: false,
      pinterestLink: false,
      other: false,
    },
  });

  const { commentText, pinterestLink, notes, referral, other, errors } = state;

  TagManager.dataLayer({
    dataLayer: {
      event: 'pageview',
      pagePath: window.location.pathname,
      pageTitle: 'appointment-booked-page',
    },
  });

  const {
    id: clientsEventId,
    clientAttributes: {
      id: clientId,
      email,
      firstName,
      lastName,
    },
    event: {
      id: eventId,
      inspirations,
    },
    upcomingAppointment: {
      appointmentType
    }
  } = clientsEventBooking;

  const { id: inspirationId } = inspirations[0] || {
    id: null,
    pinterestLink: '',
    notes: '',
  };

  const options = [
    'Friend or Family', 'Google', 'Social Media', 'Magazine', 'Other',
  ];

  const onChange = ({ target }) => {
    const { name, value } = target;

    if (name === 'notes') {
      setStylesCount(target.value.length);
    } else if (name === 'commentText') {
      setCommentCount(target.value.length);
    }

    setState({
      ...state,
      [name]: value,
      errors: {
        ...state.errors,
        [name]: !value,
      },
    });
  };

  const validComment = () => commentText.length < 250;
  const validReferral = () => referral.length < 250;

  const handleSubmit = () => {
    const valid = validComment() && validReferral();
    const whoReferred = other !== '' ? other : referral;

    if (!valid) {
      setState({
        ...state,
        errors: {
          notes: notes.length >= 250,
          commentText: commentText.length >= 250,
          referral: (other.length > 250),
        },
      });
    } else {
      const input = {
        id: clientsEventId,
        clientAttributes: {
          id: clientId,
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          referral: whoReferred,
        },
        eventAttributes: {
          id: eventId,
          inspirationsAttributes: [{ id: inspirationId, pinterestLink: pinterestLink.trim(), notes: notes.trim() }],
        },
      };

      if (state.commentText) {
        input.eventAttributes.commentsAttributes = [{
          text: commentText.trim(),
          commentableId: eventId,
          commentableType: 'Event',
          writtenById: clientId,
          writtenByType: 'Client',
        }];
      }

      mutate(input, { canProceed: true, thankyou: true });
    }
  };

  const Title = () => (
    <div className="text-container add-padding-bottom">
      <h1>You're all set</h1>
      <p>Help us get ready for your appointment</p>
    </div>
  );

  window.addEventListener('beforeunload', (e) => {
    e.returnValue = 'Form is not complete. Are you sure you want to leave?';
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const script = document.createElement('script');

      script.src = 'https://181302.t.hyros.com/v1/lst/universal-script?ph=9ce99df8788751883d56ec077d783be0e2385d3244728ba58b2543d3f3c3fb7b&tag=!clicked';

      script.async = true;

      document.body.appendChild(script);

      return () => {
        // clean up the script when the component in unmounted
        document.body.removeChild(script);
      };
    }
  }, []);

  useEffect(() => {
    ReactGA.initialize('G-CMT6EYPT3K');
    ReactGA.event({
      category: 'booker',
      action: 'appointment_confirmed',
    });
  }, []);

  const renderConfirmationFBPixel = () => {
    if (process.env.NODE_ENV === 'production') {
      ReactPixel.init('527878900693629');
      ReactPixel.track('Schedule');
      return true;
    }
    return true;
  };

  const moveToCreateAccount = () => {
    window.location.replace(`${process.env.PROXY_PATH}/profile/clients/sign_up?client[email]=${email}`);
  };

  const StylesLink = () => {
    switch (appointmentType) {
      case 'Bridal First Appointment':
        return (
          <Link href="https://amsale.com/pages/amsale-bridal" target="_blank" className="external-link">
            Follow the link to see our core styles
            <img src={ExternalLinkIcon} alt="External Link" />
          </Link>
        );
      case 'Bridesmaids First Appointment':
        return (
          <Link href="https://amsale.com/collections/amsale-bridesmaids" target="_blank" className="external-link">
            Follow the link to see our core styles
            <img src={ExternalLinkIcon} alt="External Link" />
          </Link>
        );
      case 'Little White Dress First Appointment':
        return (
          <Link href="https://amsale.com/collections/little-white-dresses " target="_blank" className="external-link">
            Follow the link to see our core styles
            <img src={ExternalLinkIcon} alt="External Link" />
          </Link>
        );
      case 'Evening First Appointment':
        return (
          <Link href="https://amsale.com/collections/evening" target="_blank" className="external-link">
            Follow the link to see our core styles
            <img src={ExternalLinkIcon} alt="External Link" />
          </Link>
        );
      default:
        return 'Error';
    }
  }

  return (
    <div className="appointment-confirmation-wrapper">
      <Title />
      <Grid container justify="center" spacing="3">
        <Grid item xs={12}>
          <div className="mobile-img-wrapper">
            <img src={BookingConfirmed} alt="Booking confirmed"/>
          </div>
        </Grid>
        <Grid item xs={12} md={12}>
          <InputMui
            fullWidth
            multiline
            rows={3}
            rowsMax={5}
            value={notes}
            name="notes"
            label="Please list or link 3-5 styles you would like to try"
            onChange={onChange}
            error={errors.notes}
            inputProps={{ maxLength: 250 }}
            helperText={errors.notes ? 'At least 10 but less than 250 characters required' : ''}
          />
          <div className="textarea-flex">
            <span className="textarea-word-count">Characters {stylesCount} / 250</span>
            <StylesLink />
            {/*<Link href="#" target="_blank" className="external-link">*/}
            {/*  Follow the link to see our core styles*/}
            {/*  <img src={ExternalLinkIcon} alt="External Link"/>*/}
            {/*</Link>*/}
          </div>
        </Grid>
        <Grid item xs={12} md={12}>
          <InputMui
            fullWidth
            value={pinterestLink}
            name="pinterestLink"
            label="Link to Pinterest or Wedding Website"
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <InputMui
            fullWidth
            multiline
            rows={3}
            rowsMax={5}
            value={commentText}
            name="commentText"
            label="Is there anything additional you’d like us to know (must-haves, preferred silhouettes, event details…)"
            onChange={onChange}
            error={errors.commentText}
            inputProps={{ maxLength: 250 }}
            helperText={errors.commentText ? 'At least 10 but less than 250 characters required' : ''}
          />
          <div className="textarea-flex">
            <span className="textarea-word-count">Characters {commentCount} / 250</span>
          </div>
        </Grid>
        <Grid item xs={12} md={12}>
          <SelectMui
            options={options}
            value={referral}
            name="referral"
            label="How did you hear about us?"
            onChange={onChange}
          />
        </Grid>
        {referral === 'Other' && (
          <Grid item xs={12}>
            <InputMui
              fullWidth
              multiline
              rows={3}
              rowsMax={5}
              value={other}
              name="other"
              label="Tell us how did you hear about us."
              onChange={onChange}
              error={errors.referral}
              helperText={errors.referral ? 'Less than 250 characters' : ''}
            />
          </Grid>
        )}
      </Grid>
      <Grid container className="confirmed-actions-wrapper">
        <div className="form-btn left-align primary-btn">
          <Button onClick={handleSubmit}>Submit</Button>
        </div>
        {!currentClientId &&
        <Link onClick={moveToCreateAccount} className="create-account-btn">Create an Account</Link>}
      </Grid>
      {renderConfirmationFBPixel()}
    </div>
  );
};


export default AppointmentConfirmed;
